import { FormErrorsResponse } from 'interfaces/component'
import { ReduxFormErrors } from 'interfaces'
import { packFormErrors } from 'helpers/formHelpers'
import { Observable, of, merge, EMPTY } from 'rxjs'
import { AjaxError } from 'rxjs/ajax'
import { CONFIRM_MEMBER_CREDIT_URL, SET_MEMBER_CREDIT_EXTENDED_URL, SET_MEMBER_CREDIT_URL } from 'interfaces/ajax'
import { CreditLinkagePhase } from 'interfaces/credit'
import { Action } from 'actions'
import { ABORTED_STATUS_CODE, FORBIDDEN_STATUS_CODE, SERVICE_UNAVAILABLE_CODE, UNAUTHORIZED_STATUS_CODE } from 'constants/ajax'
import { ONBOARDING_STEP_ANCHOR_BY_NAME, ONBOARDING_STEP_URL_BY_NAME } from 'constants/onboarding'
import { activateAnchorActionCreator, pushActionCreator } from 'actions/navigationActions'
import { setCreditErrorActionCreator } from 'actions/creditActions'
import { TUCI_INTERNAL_ERROR, TUCI_VERIFICATION_FAILED } from 'constants/errors'
import { captureEndpointError, captureException, getErrorType, isAjaxError } from 'helpers/errorHelpers'
import { UnexpectedInternalError } from 'interfaces/errors'
import { ForbiddenApiResponse } from 'interfaces/ajax/payload'
import { handleUnauthorizedErrorActionCreator, handleNetworkErrorActionCreator, setUnexpectedErrorActionCreator } from 'actions/globalActions'
import { isDashboardPage } from 'helpers/pageHelpers'
import { DASHBOARD_URL } from 'tangle-shared'
import { GetSubscriptionAction } from 'actions/subscriptionActions'
import { Unobservable } from 'rxOperators/interfaces'

export const getResponseFormErrors = (responseErrors: FormErrorsResponse = {}): ReduxFormErrors => {
  return Object.keys(responseErrors)
    .reduce((result, key) => ({
      ...result,
      [key === 'non_field_errors' ? '_error' : key]: packFormErrors(responseErrors[key]),
    }), {})
}

const ABANDONED_ERROR_CODES = new Set([ABORTED_STATUS_CODE, UNAUTHORIZED_STATUS_CODE])
const EXPECTED_ERROR_CODES = new Set(Array.from(ABANDONED_ERROR_CODES).concat(FORBIDDEN_STATUS_CODE))

export const isAbandonedError = ({ status }: AjaxError): boolean =>
  ABANDONED_ERROR_CODES.has(status)

export const isExpectedError = ({ status }: AjaxError): boolean =>
  EXPECTED_ERROR_CODES.has(status)

export type CatchFunction = (error: UnexpectedInternalError, silent?: boolean) => Observable<Action>

const processForbiddenAjaxErrorInner = (xhr: XMLHttpRequest) => {
  if (xhr && xhr.response && xhr.response.onboarding_step) {
    const { onboarding_step } = xhr.response as ForbiddenApiResponse
    const url = ONBOARDING_STEP_URL_BY_NAME[onboarding_step]

    if (url) {
      return merge(
        of(pushActionCreator(url)),
        of(activateAnchorActionCreator(ONBOARDING_STEP_ANCHOR_BY_NAME[onboarding_step])),
      )
    }
  }

  if (!isDashboardPage()) {
    return merge(
      of(pushActionCreator(DASHBOARD_URL)),
      of(new GetSubscriptionAction()),
    )
  }

  return EMPTY
}

// fix for some weird typescript type checking issue
type ProcessForbiddenAjaxErrorInner = Unobservable<ReturnType<typeof processForbiddenAjaxErrorInner>>
export const processForbiddenAjaxError = (xhr: XMLHttpRequest): Observable<ProcessForbiddenAjaxErrorInner> => {
  return processForbiddenAjaxErrorInner(xhr)
}

export const processError: CatchFunction = (error, silent) => {
  if (!isAjaxError(error)) {
    captureException(error)

  } else {
    const { status, xhr } = error

    if (status === ABORTED_STATUS_CODE) {
      return of(handleNetworkErrorActionCreator())
    }

    if (status === UNAUTHORIZED_STATUS_CODE) {
      return of(handleUnauthorizedErrorActionCreator())
    }

    if (status === FORBIDDEN_STATUS_CODE) {
      const handleForbiddenAjax$ = processForbiddenAjaxError(xhr)
      if (handleForbiddenAjax$) {
        return handleForbiddenAjax$
      }
    }

    captureEndpointError(error)
  }

  return of(setUnexpectedErrorActionCreator(getErrorType(error), error, silent))
}

// ===========================================================================//
//                                ONBOARDING CREDIT                           //
// ===========================================================================//

const creditUrlPhaseMap: Record<string, CreditLinkagePhase> = {
  [SET_MEMBER_CREDIT_URL]: 'form-simple',
  [SET_MEMBER_CREDIT_EXTENDED_URL]: 'form-extended',
  [CONFIRM_MEMBER_CREDIT_URL]: 'questions',
}
export const processTUCIError: CatchFunction = (error) => {
  if (!isAjaxError(error)) {
    captureException(error)

    return of(setUnexpectedErrorActionCreator(getErrorType(error), error))
  }

  const { status, request } = error
  const phase = request.url
    ? creditUrlPhaseMap[request.url.split('?')[0]]
    : null

  const errorCode = status === SERVICE_UNAVAILABLE_CODE
    ? TUCI_INTERNAL_ERROR
    : TUCI_VERIFICATION_FAILED

  return of(setCreditErrorActionCreator(errorCode, phase))
}
